import { SaveOutlined, SyncOutlined } from "@ant-design/icons";
import { Checkbox, Col, Input, Row, Select, Switch } from "antd";
import { useFormik } from "formik";
import { get, includes, isEmpty, map } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import { setSection } from "../../../redux/ducks/Section";
import CostCentre from "../../../services/api/CostCentre";
import PfpService from "../../../services/api/PfpService";
import User from "../../../services/api/User";
import UserActiveDirectory from "../../../services/api/UserActiveDirectory";
import { roles as rolesObject } from "../../../utils/properties";
import { showError, showSuccess, trimData } from "../../../utils/utilities";
import SalSubstituteComponent from "../../../components/hooks_components/SalSubstituteComponent";

const InternalUserDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const isNewInternal = params.internalUsername === "new-internal";
  const pageIsLoading = useSelector((state) => get(state.loadingDuck, "pageIsLoading", false));

  // States
  const [userData, setUserData] = useState({});
  const [syncIsLoading, setSyncIsLoading] = useState(false);
  const [selectOptions, setSelectOptions] = useState({
    serviceId: [],
    substitute: [],
    expenseRefundUserToSubstitute: [],
    costCentreAccount: [],
  });
  const formikDetailInternal = useFormik({
    initialValues: {
      username: null,
      name: null,
      surname: null,
      email: null,
      cid: null,
      serviceId: null,
      roles: null,
      enabled: false,
      costCentreAccount: null,
      checkAdp: true,
      costCentresDM: [],
      costCentresSuperPM: [],
      costCentresTimesheetApprover: [],
      userToSubstitute: [],
      expenseRefundUserToSubstitute: [],
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.username) {
        errors.username = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      if (!values.surname) {
        errors.surname = "Required!";
      }
      if (!values.email) {
        errors.email = "Required!";
      }
      if (!values.cid) {
        errors.cid = "Required!";
      }
      if (isEmpty(values.roles)) {
        errors.roles = "Required!";
      }
      if (values.roles && values.roles.find((e) => e === "TIMESHEET") && !values.serviceId) {
        errors.serviceId = "Required!";
      }
      if (values.roles && values.roles.find((e) => e === "TIMESHEET_SUBSTITUTE") && isEmpty(values.userToSubstitute)) {
        errors.userToSubstitute = "Required!";
      }
      if (
        values.roles &&
        values.roles.find((e) => e === "EXPENSE_REFUND_SUBST") &&
        isEmpty(values.expenseRefundUserToSubstitute)
      ) {
        errors.expenseRefundUserToSubstitute = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      let dataSave = {
        ...values,
        costCentreAccount: isEmpty(values.costCentreAccount)
          ? null
          : selectOptions.costCentreAccount.find((cc) => cc.id === values.costCentreAccount),
        costCentresDM: isEmpty(values.costCentresDM)
          ? null
          : values.costCentresDM.map((ccDM) => selectOptions.costCentreAccount.find((cc) => cc.id === ccDM)),
        costCentresSuperPM: isEmpty(values.costCentresSuperPM)
          ? null
          : values.costCentresSuperPM.map((ccsPM) => selectOptions.costCentreAccount.find((cc) => cc.id === ccsPM)),
        costCentresTimesheetApprover: isEmpty(values.costCentresTimesheetApprover)
          ? null
          : values.costCentresTimesheetApprover.map((ccTA) =>
              selectOptions.costCentreAccount.find((cc) => cc.id === ccTA)
            ),
        expenseRefundUserToSubstitute: isEmpty(values.expenseRefundUserToSubstitute)
          ? null
          : values.expenseRefundUserToSubstitute.map((uTS) =>
              selectOptions.expenseRefundUserToSubstitute.find((er) => er.cid === uTS)
            ),
        userToSubstitute: isEmpty(values.userToSubstitute)
          ? null
          : values.userToSubstitute.map((sub) => selectOptions.substitute.find((s) => s.cid === sub)),
        type: "INTERNAL",
      };
      let saveResponse = null;
      if (isNewInternal) {
        // Creo
        saveResponse = await User.saveInternalUser(dataSave);
      } else {
        // Modifico
        saveResponse = await User.saveInternalUserEdit(values.username, dataSave);
      }
      if (!saveResponse.error) {
        showSuccess();
        if (
          includes(formikDetailInternal.values.roles, "SAL_SUBSTITUTE") &&
          !includes(userData.roles, "SAL_SUBSTITUTE")
        ) {
          // Se ho aggiunto il ruolo sostituto sal, non faccio il redirect in quanto devo rimanere per fare l'assegnazione
          getCurrentInternalData(saveResponse.data.username);
        } else {
          navigate("/internal-users");
        }
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    },
  });
  useEffect(() => {
    dispatch(setLoading(true));
    const getCostCenter = async () => {
      let returnData = [];
      const { data, error } = await CostCentre.costCentre("allPages=true");
      if (!error) {
        returnData = data;
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
      return returnData;
    };
    const getServices = async () => {
      let returnData = [];
      const { error, data } = await PfpService.getServicesList("allPages=true");
      if (!error && data) {
        returnData = data;
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
      return returnData;
    };
    if (!isNewInternal) {
      getCurrentInternalData();
    }
    const fetchData = async () => {
      // Recupero tutti i dati per popolare le select una sola volta per evitare sovrascritture
      const costCenter = await getCostCenter();
      const services = await getServices();
      const substitute = await getInternalList(!isNewInternal ? params.internalUsername : null);
      const expenseRefundUserToSubstitute = await getexpenseRefundUserToSubstituteInternalList(
        !isNewInternal ? params.internalUsername : null
      );
      setSelectOptions({
        ...selectOptions,
        serviceId: services,
        costCentreAccount: costCenter,
        substitute,
        expenseRefundUserToSubstitute,
      });
    };
    fetchData();
    dispatch(
      setSection({
        section: {
          title: params.internalUsername === "new-internal" ? "userInternal.newInternal" : params.internalUsername,
        },
      })
    );
    dispatch(setLoading(false));
  }, []);
  useEffect(() => {
    // Recupero i valori della select solo se sono in creazione (altrimenti la chiamata l'ho già fatta nell'altro useeffect), ho selezionato il ruolo interessato e se la select non è già popolata
    const fetchData = async () => {
      let substitute = selectOptions.substitute;
      let expenseRefundUserToSubstitute = selectOptions.expenseRefundUserToSubstitute;
      if (
        formikDetailInternal.values.roles.find((e) => e === "TIMESHEET_SUBSTITUTE") &&
        isEmpty(selectOptions.substitute)
      ) {
        substitute = await getInternalList(formikDetailInternal.values.username);
      }
      if (
        formikDetailInternal.values.roles.find((e) => e === "EXPENSE_REFUND_SUBST") &&
        isEmpty(selectOptions.expenseRefundUserToSubstitute)
      ) {
        expenseRefundUserToSubstitute = await getexpenseRefundUserToSubstituteInternalList(
          formikDetailInternal.values.username
        );
      }
      if (!isEmpty(substitute) || !isEmpty(expenseRefundUserToSubstitute)) {
        setSelectOptions({ ...selectOptions, substitute, expenseRefundUserToSubstitute });
      }
    };
    if (formikDetailInternal.values.roles && isNewInternal) {
      fetchData();
    }
  }, [formikDetailInternal.values.roles]);
  const getInternalList = async (username = null) => {
    let substitute = [];
    if (username) {
      const { data, error } = await User.getValidSubstituteUsers(username);
      if (!error && data) {
        substitute = data;
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
    }
    return substitute;
  };
  const getexpenseRefundUserToSubstituteInternalList = async (username = null) => {
    let expenseRefundUserToSubstitute = [];
    if (username) {
      const { data, error } = await User.getValidexpenseRefundUserToSubstituteUsers(username);
      if (!error && data) {
        expenseRefundUserToSubstitute = data;
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
    }
    return expenseRefundUserToSubstitute;
  };
  const getCurrentInternalData = async (username = params.internalUsername) => {
    dispatch(setLoading(true));
    const { data, error } = await User.getUserData(username);
    if (!error && data) {
      setUserData(data);
      formikDetailInternal.setFieldValue("username", get(data, "username", ""));
      formikDetailInternal.setFieldValue("cid", get(data, "cid", ""));
      formikDetailInternal.setFieldValue("name", get(data, "name", ""));
      formikDetailInternal.setFieldValue("surname", get(data, "surname", ""));
      formikDetailInternal.setFieldValue("email", get(data, "email", ""));
      formikDetailInternal.setFieldValue("costCentreAccount", get(data, "costCentreAccount.id", null));
      formikDetailInternal.setFieldValue("serviceId", get(data, "serviceId", null));
      formikDetailInternal.setFieldValue("roles", get(data, "roles", ["EXPENSE_REFUND"]));
      formikDetailInternal.setFieldValue("enabled", get(data, "enabled", false));
      formikDetailInternal.setFieldValue("checkAdp", get(data, "checkAdp", true));
      formikDetailInternal.setFieldValue("userToSubstitute", map(get(data, "userToSubstitute", []), "cid"));
      formikDetailInternal.setFieldValue("costCentresDM", map(get(data, "costCentresDM", []), "id"));
      formikDetailInternal.setFieldValue("costCentresSuperPM", map(get(data, "costCentresSuperPM", []), "id"));
      formikDetailInternal.setFieldValue(
        "costCentresTimesheetApprover",
        map(get(data, "costCentresTimesheetApprover", []), "id")
      );
      formikDetailInternal.setFieldValue(
        "expenseRefundUserToSubstitute",
        map(get(data, "expenseRefundUserToSubstitute", []), "cid")
      );

      // setState((prevState) => ({
      //   ...prevState,
      //   currentInternalData: currentInternalData.data,
      //   selectedCostCenter: get(currentInternalData.data, "costCentreAccount", null),
      //   selectedDMCostCenters: get(currentInternalData.data, "costCentresDM", []),
      //   selectedSuperPMCostCenters: get(currentInternalData.data, "costCentresSuperPM", []),
      //   selectedTimesheetApproverCostCenters: currentInternalData.data?.costCentresTimesheetApprover === null ? [] : get(currentInternalData.data, "costCentresTimesheetApprover", []),
      //   checkedRoles: get(currentInternalData.data, "roles", []),
      // }));
    } else if (data === null) {
      // setState((prevState) => ({
      //   ...prevState,
      //   currentInternalData: [],
      //   selectedCostCenter: null,
      //   selectedDMCostCenters: [],
      //   selectedSuperPMCostCenters: [],
      //   selectedTimesheetApproverCostCenters: [],
      //   checkedRoles: []
      // }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };
  const getUserData = async () => {
    formikDetailInternal.resetForm();
    setSyncIsLoading(true);
    const { error, data } = await UserActiveDirectory.getUserDataFromActiveDirectory(
      formikDetailInternal.values.username
    );
    if (!error && data) {
      getCurrentInternalData(data.username);
      formikDetailInternal.setFieldValue("username", get(data, "username", ""));
      formikDetailInternal.setFieldValue("cid", get(data, "cid", ""));
      formikDetailInternal.setFieldValue("name", get(data, "name", ""));
      formikDetailInternal.setFieldValue("surname", get(data, "surname", ""));
      formikDetailInternal.setFieldValue("email", get(data, "email", ""));
      formikDetailInternal.setFieldValue("costCentreAccount", get(data, "costCentreAccount.id", null));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setSyncIsLoading(false);
  };
  return (
    <div className="padding-30">
      <Row gutter={[48, 32]}>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="username">
              {t("general.Username")}*
            </label>
          </Row>
          <Row type="flex" justify="space-between">
            <Col lg={18} md={14} xs={16} sm={16}>
              <Input
                disabled={!isNewInternal}
                id="username"
                name="username"
                placeholder={t("general.Username")}
                onChange={formikDetailInternal.handleChange}
                onBlur={formikDetailInternal.handleBlur}
                value={formikDetailInternal.values.username}
              />
            </Col>
            <Col>
              <Button
                currentIcon={<SyncOutlined className="icon-medium" />}
                clickCallback={() => getUserData()}
                isLoading={syncIsLoading}
                isDisabled={isEmpty(formikDetailInternal.values.username)} //  || state.saveIsLoading
              />
            </Col>
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="name">
              {t("userInternal.name")}*
            </label>
          </Row>
          <Row>
            <Input
              disabled
              id="name"
              name="name"
              placeholder={t("userInternal.name")}
              onChange={formikDetailInternal.handleChange}
              onBlur={formikDetailInternal.handleBlur}
              value={formikDetailInternal.values.name}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="surname">
              {t("userInternal.surname")}*
            </label>
          </Row>
          <Row>
            <Input
              disabled
              id="surname"
              name="surname"
              placeholder={t("userInternal.surname")}
              onChange={formikDetailInternal.handleChange}
              onBlur={formikDetailInternal.handleBlur}
              value={formikDetailInternal.values.surname}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="email">
              {t("userInternal.email")}*
            </label>
          </Row>
          <Row>
            <Input
              disabled
              id="email"
              name="email"
              placeholder={t("userInternal.email")}
              onChange={formikDetailInternal.handleChange}
              onBlur={formikDetailInternal.handleBlur}
              value={formikDetailInternal.values.email}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="cid">
              {t("userInternal.cid")}*
            </label>
          </Row>
          <Row>
            <Input
              disabled
              id="cid"
              name="cid"
              placeholder={t("userInternal.cid")}
              onChange={formikDetailInternal.handleChange}
              onBlur={formikDetailInternal.handleBlur}
              value={formikDetailInternal.values.cid}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="service">
              {t("userInternal.service")}{" "}
              {includes(formikDetailInternal.values.roles, "TIMESHEET") && `* ${t("userInternal.required")}`}
            </label>
          </Row>
          <Row className="mb-2em">
            <Select
              id="serviceId"
              name="serviceId"
              className={"w-100" + (formikDetailInternal.errors.serviceId !== undefined ? " input-error" : "")}
              style={{ borderRadius: "6px" }}
              showSearch
              allowClear={!includes(formikDetailInternal.values.roles, "TIMESHEET")}
              onChange={(s) => formikDetailInternal.setFieldValue("serviceId", s)}
              placeholder={t("userInternal.service")}
              onBlur={formikDetailInternal.handleBlur("serviceId")}
              value={formikDetailInternal.values.serviceId}
              options={selectOptions.serviceId.map((d) => ({ value: d.id, label: d.id }))}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="costCentreAccount">
              {t("userInternal.costCenterAccount")}
            </label>
          </Row>
          <Row>
            <Select
              allowClear
              id="costCentreAccount"
              name="costCentreAccount"
              className="w-100"
              showSearch
              placeholder={t("userInternal.costCenterAccount")}
              // onChange={(value, optionData) => { changeAccountCostCenter(value, optionData); }}
              onChange={(c) => formikDetailInternal.setFieldValue("costCentreAccount", c)}
              onBlur={formikDetailInternal.handleBlur("costCentreAccount")}
              value={formikDetailInternal.values.costCentreAccount}
              options={selectOptions.costCentreAccount.map((d) => ({ value: d.id, label: d.id }))}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="enabled">
              {t("general.enabled")}
            </label>
          </Row>
          <Row>
            <Switch
              id="enabled"
              name="enabled"
              onChange={() => {
                formikDetailInternal.setFieldValue("enabled", !formikDetailInternal.values.enabled);
              }}
              checked={formikDetailInternal.values.enabled}
            />
          </Row>
        </Col>
        {includes(formikDetailInternal.values.roles, "TIMESHEET") && (
          <Col lg={8} md={8} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="checkAdp">
                {t("userInternal.presence")}
              </label>
            </Row>
            <Row>
              <Switch
                id="checkAdp"
                name="checkAdp"
                onChange={() => {
                  formikDetailInternal.setFieldValue("checkAdp", !formikDetailInternal.values.checkAdp);
                }}
                checked={formikDetailInternal.values.checkAdp}
              />
            </Row>
          </Col>
        )}
      </Row>
      <Row style={{ marginTop: "25px" }}>
        {/** Suddivido in 2 la schermata. Così se clicco un ruolo che necessita di campi in più saranno mostrati nella metà dello schermo */}
        <Col lg={8} md={8} xs={24} sm={8}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="roles">
              {t("userInternal.roles")}*
            </label>
          </Row>
          <Row>
            <Checkbox.Group
              id="roles"
              name="roles"
              disabled={!formikDetailInternal.values.username}
              value={formikDetailInternal.values.roles}
              onChange={(checkedValues) => {
                formikDetailInternal.setFieldValue("roles", checkedValues);

                if (includes(checkedValues, "TIMESHEET")) {
                  formikDetailInternal.validateField("serviceId");
                }
              }}
            >
              <div>
                {Object.values(rolesObject)
                  .filter((value) => value !== "SUPPLIER")
                  .map((value) => (
                    <div key={value}>
                      <Checkbox
                        disabled={value === "SAL_SUBSTITUTE" && !userData.canRemoveSALSubstituteRole}
                        value={value}
                      >
                        {t(`roles.${value}`)}
                      </Checkbox>
                    </div>
                  ))}
              </div>
            </Checkbox.Group>
          </Row>
        </Col>
        <Col lg={16} md={16} xs={24} sm={16}>
          <Row gutter={[48, 32]}>
            {/** Se seleziono il DM */}
            {includes(formikDetailInternal.values.roles, "DM") && (
              <Col lg={12} md={12} xs={24} sm={12}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="costCentresDM">
                    {t("userInternal.costCentersDM")}
                  </label>
                </Row>
                <Row>
                  <Select
                    id="costCentresDM"
                    name="costCentresDM"
                    className="w-100"
                    placeholder={t("userInternal.costCentersDM")}
                    value={formikDetailInternal.values.costCentresDM}
                    onBlur={formikDetailInternal.handleBlur}
                    onChange={(value) => {
                      formikDetailInternal.setFieldValue("costCentresDM", value);
                    }}
                    mode="multiple"
                    maxTagCount="responsive"
                    options={selectOptions.costCentreAccount.map((d) => ({ value: d.id, label: d.id }))}
                  />
                </Row>
              </Col>
            )}
            {/** Se seleziono il SUPER_PM */}
            {includes(formikDetailInternal.values.roles, "SUPER_PM") && (
              <Col lg={12} md={12} xs={24} sm={12}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="costCentresSuperPM">
                    {t("userInternal.costCentersSuperPM")}
                  </label>
                </Row>
                <Row>
                  <Select
                    id="costCentresSuperPM"
                    name="costCentresSuperPM"
                    className="w-100"
                    placeholder={t("userInternal.costCentersSuperPM")}
                    onBlur={formikDetailInternal.handleBlur("costCentresSuperPM")}
                    onChange={(value) => {
                      formikDetailInternal.setFieldValue("costCentresSuperPM", value);
                    }} // onChange={(value, option) => {
                    //   changeSuperPMCostCenters(value, option);
                    // }}
                    value={formikDetailInternal.values.costCentresSuperPM}
                    mode="multiple"
                    maxTagCount="responsive"
                    options={selectOptions.costCentreAccount.map((d) => ({ value: d.id, label: d.id }))}
                  />
                </Row>
              </Col>
            )}
            {/** Se seleziono il TIMESHEET_APPROVER */}
            {includes(formikDetailInternal.values.roles, "TIMESHEET_APPROVER") && (
              <Col lg={12} md={12} xs={24} sm={12}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="costCentresTimesheetApprover">
                    {t("userInternal.costCentersTimesheetApprover")}
                  </label>
                </Row>
                <Row>
                  <Select
                    id="costCentresTimesheetApprover"
                    name="costCentresTimesheetApprover"
                    className="w-100"
                    placeholder={t("userInternal.costCentersTimesheetApprover")}
                    onBlur={formikDetailInternal.handleBlur}
                    onChange={(value) => {
                      formikDetailInternal.setFieldValue("costCentresTimesheetApprover", value);
                    }}
                    value={formikDetailInternal.values.costCentresTimesheetApprover}
                    mode="multiple"
                    maxTagCount="responsive"
                    options={selectOptions.costCentreAccount.map((d) => ({ value: d.id, label: d.id }))}
                  />
                </Row>
              </Col>
            )}
            {/** Se seleziono il TIMESHEET_SUBSTITUTE */}
            {includes(formikDetailInternal.values.roles, "TIMESHEET_SUBSTITUTE") && (
              <Col lg={12} md={12} xs={24} sm={12}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="userToSubstitute">
                    {t("userInternal.substitute")}*
                  </label>
                </Row>
                <Row className="mb-2em">
                  <Select
                    mode="multiple"
                    className="w-100"
                    id="userToSubstitute"
                    name="userToSubstitute"
                    showSearch
                    optionFilterProp="label"
                    onChange={(value) => formikDetailInternal.setFieldValue("userToSubstitute", value)}
                    placeholder={t("userInternal.substitute")}
                    onBlur={formikDetailInternal.handleBlur("userToSubstitute")}
                    value={formikDetailInternal.values.userToSubstitute}
                    options={selectOptions.substitute.map((d) => ({ value: d.cid, label: `${d.name} ${d.surname}` }))}
                  />
                </Row>
              </Col>
            )}
            {/** Se seleziono il EXPENSE_REFUND_SUBST */}
            {includes(formikDetailInternal.values.roles, "EXPENSE_REFUND_SUBST") && (
              <Col lg={12} md={12} xs={24} sm={12}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="expenseRefundUserToSubstitute">
                    {t("userInternal.expenseRefundUserToSubstitute")}*
                  </label>
                </Row>
                <Row className="mb-2em">
                  <Select
                    mode="multiple"
                    className="w-100"
                    id="expenseRefundUserToSubstitute"
                    name="expenseRefundUserToSubstitute"
                    showSearch
                    optionFilterProp="label"
                    onChange={(value) => formikDetailInternal.setFieldValue("expenseRefundUserToSubstitute", value)}
                    placeholder={t("userInternal.expenseRefundUserToSubstitute")}
                    onBlur={formikDetailInternal.handleBlur("expenseRefundUserToSubstitute")}
                    value={formikDetailInternal.values.expenseRefundUserToSubstitute}
                    options={selectOptions.expenseRefundUserToSubstitute.map((d) => ({
                      value: d.cid,
                      label: `${d.name} ${d.surname}`,
                    }))}
                  />
                </Row>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      {includes(formikDetailInternal.values.roles, "SAL_SUBSTITUTE") && includes(userData.roles, "SAL_SUBSTITUTE") && (
        <SalSubstituteComponent
          username={!isNewInternal ? params.internalUsername : formikDetailInternal.values.username}
          userData={userData}
        />
      )}
      <Row gutter={16} type="flex" align="middle" justify="end" className="mt-10">
        <Col>
          {includes(formikDetailInternal.values.roles, "SAL_SUBSTITUTE") &&
            !includes(userData.roles, "SAL_SUBSTITUTE") &&
            t("salSubstituteComponent.infoTextSave")}
        </Col>
        <Col>
          <Button
            currentSize="large"
            isLoading={pageIsLoading}
            isDisabled={syncIsLoading || !(formikDetailInternal.dirty && formikDetailInternal.isValid)}
            clickCallback={formikDetailInternal.submitForm}
            currentIcon={<SaveOutlined className="icon-large" />}
          />
        </Col>
      </Row>
    </div>
  );
};

export default InternalUserDetail;
